import { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { useDataProvider, useInput } from 'react-admin';

interface FileUploadProps {
  uploadView?: React.ReactNode;
  overlayView?: React.ReactNode;
  getFileName?: (file: any) => string;
  folder: string;
  getDescription?: (file: any) => string;
  processFileResponse?: (data: any) => void;
  handleUploadError?: (error: any) => void;
  handleFileToBig?: (file: any) => void;
  processUploadedFiles?: (fileIds: number[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
  allowDragDrop?: boolean;
  fileSizeMb?: number;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

export const FileUpload = (props: FileUploadProps) => {
  const {
    uploadView,
    overlayView,
    getFileName = () => uuidv4(),
    folder,
    getDescription = (file: any) => file.name,
    processFileResponse = () => {},
    handleUploadError = () => {},
    handleFileToBig = () => {},
    processUploadedFiles = () => {},
    onUploadingChange = () => {},
    allowDragDrop = false,
    fileSizeMb = 30,
    multiple = false,
    accept = 'image/*;capture=camera;application/pdf',
    disabled = false,
    children,
  } = props;

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<any>(null);

  const dataProvider = useDataProvider();

  const setUploadingState = (state: boolean) => {
    setUploading(state);
    onUploadingChange(state);
  };

  const processFiles = async (files: any) => {
    setUploadingState(true);

    const fileIds = [];

    for (var i = 0; i < files.length; i++) {
      let file = files[i];

      if (fileSizeMb && fileSizeMb > 0) {
        const fileSize = file.size / 1048576;
        if (fileSize > fileSizeMb) {
          handleFileToBig(file);
          return;
        }
      }

      const fileName = getFileName(file);
      const res = await dataProvider.create('files', {
        data: {
          name: fileName,
          isPublic: false,
          folder: folder,
          description: getDescription(file),
          file: file,
        },
      });
      if (res && res.data) {
        fileIds.push(res.data.id);
        processFileResponse(res.data);
      } else {
        handleUploadError(res);
      }
    }
    setUploadingState(false);

    processUploadedFiles(fileIds);
  };

  const handleDrop = (event: any) => {
    if (allowDragDrop) {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        processFiles(event.dataTransfer.files);
      }
    }
  };

  const handleDragLeave = (event: any) => {
    if (allowDragDrop) {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
    }
  };

  const handleDragOver = (event: any) => {
    if (allowDragDrop) {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    }
  };

  const handleDragEnter = (event: any) => {
    if (allowDragDrop) {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    }
  };

  const handleFileChange = (event: any) => {
    event.preventDefault();
    if (event.target.files && event.target.files[0]) {
      processFiles(event.target.files);
    }
  };

  const openFileExplorer = (event: any) => {
    if (!disabled && inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  return (
    <>
      {uploading && uploadView}
      {(!uploading || (uploading && !overlayView)) && (
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={openFileExplorer}
          position="relative"
          sx={{ '&:hover': { cursor: 'pointer' } }}
        >
          <input
            id="dropzone-avatar"
            style={{ display: 'none' }}
            ref={inputRef}
            type="file"
            multiple={multiple}
            onChange={handleFileChange}
            accept={accept}
          />
          {children}
          {dragActive && overlayView}
        </Box>
      )}
    </>
  );
};

export interface RaFileUploadProps extends Omit<FileUploadProps, 'processUploadedFiles'> {
  source: string;
}

export const RaFileUpload = (props: RaFileUploadProps) => {
  const { source } = props;

  const { field } = useInput({ source });

  return (
    <FileUpload
      {...props}
      processUploadedFiles={(fileIds: number[]) => {
        field.onChange([...field.value, ...fileIds]);
      }}
    />
  );
};

export const uploadFile = (
  file: File,
  fileName: string,
  folder: string,
  description: string | null,
  dataProvider: any
) => {
  const fileSize = file.size / 1048576;
  if (fileSize > 30) {
    return Promise.reject('File size is too big');
  }

  return dataProvider.create('files', {
    data: {
      name: fileName,
      isPublic: false,
      folder: folder,
      description: description,
      file: file,
    },
  });
};
